import { useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../Roles/roles.css';
import './team.css';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { useMyOrganizationQuery } from '../../../hooks/useDashboardQueries';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUser } from '../../../hooks/useUser';
import {
  useAddOrganizationMemberMutation,
  useDeleteOrganizationRoleMutation,
  useOrganizationMembersQuery,
  useOrganizationPermissionsQuery,
  useOrganizationRolesQuery,
  useOrganizationTeamSummaryQuery,
  useRemoveOrganizationMemberMutation,
  useSaveOrganizationRoleMutation,
  useSetOrganizationMemberRoleMutation,
} from '../../../hooks/useOrganizationTeamQueries';
import {
  MY_TEAM,
  OrganizationMemberV2,
  OrganizationRoleV2,
  TeamScope,
} from '../../../types/organizationTeam';
import { AddMemberDialog } from './AddMemberDialog';
import { TeamMembersPanel, memberName } from './TeamMembersPanel';
import { TeamRoleDialog } from './TeamRoleDialog';
import { TeamRolesPanel } from './TeamRolesPanel';
import { teamErrorText } from './teamErrorText';

type PendingConfirm =
  | { kind: 'member'; member: OrganizationMemberV2 }
  | { kind: 'role'; role: OrganizationRoleV2 };

/**
 * An organization's team: its members and the roles they hold.
 * /dashboard/team — the owner's own organization.
 * /dashboard/organizations/:organizationId/team — any organization, for staff
 * with organizations.manage (members are added from User Details there).
 */
export const TeamV2 = () => {
  const { t } = useTranslation();
  const errorText = (error: unknown) => (error ? teamErrorText(error, t) : undefined);
  const { organizationId } = useParams<{ organizationId?: string }>();
  const { has } = usePermissions();
  // Permissions are empty until the profile arrives; that is not "no access".
  const { user } = useUser();
  const isStaff = Boolean(organizationId);
  const scope = useMemo<TeamScope>(
    () => (organizationId ? { kind: 'org', orgId: organizationId } : MY_TEAM),
    [organizationId],
  );

  const myOrg = useMyOrganizationQuery(!isStaff);
  const allowed = isStaff ? Boolean(user) && has('organizations.manage') : Boolean(myOrg.data);
  const summary = useOrganizationTeamSummaryQuery(organizationId, isStaff && allowed);
  const orgName = isStaff ? summary.data?.name : myOrg.data?.name;

  const roles = useOrganizationRolesQuery(scope, allowed);
  const members = useOrganizationMembersQuery(scope, allowed);
  const permissions = useOrganizationPermissionsQuery(scope, allowed);

  const saveRole = useSaveOrganizationRoleMutation(scope);
  const deleteRole = useDeleteOrganizationRoleMutation(scope);
  const addMember = useAddOrganizationMemberMutation(scope);
  const setMemberRole = useSetOrganizationMemberRoleMutation(scope);
  const removeMember = useRemoveOrganizationMemberMutation(scope);

  const [roleDialog, setRoleDialog] = useState<{ open: boolean; role: OrganizationRoleV2 | null }>({ open: false, role: null });
  const [isAddOpen, setAddOpen] = useState(false);
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  if (isStaff && !user) return null;
  if (isStaff && !has('organizations.manage')) {
    return <EmptyState icon="lock" message={t('dashboard.team.noAccess')} />;
  }
  if (!isStaff && myOrg.isLoading) return null;
  if (!isStaff && !myOrg.data) {
    return <EmptyState icon="groups" message={t('dashboard.team.noOrganization')} />;
  }

  const failed = summary.isError || roles.isError || members.isError;
  if (failed) {
    return (
      <ErrorMessage
        message={t('dashboard.team.loadError')}
        onRetry={() => {
          if (summary.isError) void summary.refetch();
          if (roles.isError) void roles.refetch();
          if (members.isError) void members.refetch();
        }}
      />
    );
  }
  if (roles.isLoading || members.isLoading || (isStaff && summary.isLoading)) return null;

  const roleList = roles.data ?? [];
  const memberList = members.data ?? [];

  const openRoleDialog = (role: OrganizationRoleV2 | null) => {
    saveRole.reset();
    setRoleDialog({ open: true, role });
  };

  const submitRole = async (payload: { name: string; permissionIds: string[] }) => {
    try {
      await saveRole.mutateAsync({ id: roleDialog.role?.id, payload });
      setRoleDialog({ open: false, role: null });
    } catch {
      // Shown from saveRole.error.
    }
  };

  const confirmPending = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'member') await removeMember.mutateAsync(pending.member.id);
      else await deleteRole.mutateAsync(pending.role.id);
      setPending(null);
    } catch {
      // Shown from the mutation's error.
    }
  };

  const closePending = () => {
    removeMember.reset();
    deleteRole.reset();
    setPending(null);
  };

  return (
    <div className="team-page">
      <header className="team-header">
        <div>
          <h1 className="team-title">{orgName ?? '…'}</h1>
          <p className="team-muted">
            {t('dashboard.team.subtitle', { members: memberList.length, roles: roleList.length })}
          </p>
        </div>
        {!isStaff && (
          <button
            type="button"
            className="primary-action-btn"
            onClick={() => setAddOpen(true)}
            disabled={roleList.length === 0}
            title={roleList.length === 0 ? t('dashboard.team.members.needRole') : undefined}
          >
            {t('dashboard.team.members.add')}
          </button>
        )}
      </header>

      <div className="team-layout">
        <section className="team-panel team-members" aria-labelledby="team-members-title">
          <header className="team-panel-header">
            <h2 id="team-members-title" className="team-panel-title">
              {t('dashboard.team.members.title', { count: memberList.length })}
            </h2>
          </header>
          {isStaff && <p className="team-muted team-staff-hint">{t('dashboard.team.members.staffAddHint')}</p>}
          <TeamMembersPanel
            members={memberList}
            roles={roleList}
            onChangeRole={(userId, roleId) => setMemberRole.mutateAsync({ userId, roleId })}
            onRemove={(member) => setPending({ kind: 'member', member })}
            isSaving={setMemberRole.isPending}
          />
        </section>

        <TeamRolesPanel
          roles={roleList}
          onCreate={() => openRoleDialog(null)}
          onEdit={(r) => openRoleDialog(r)}
          onDelete={(role) => setPending({ kind: 'role', role })}
        />
      </div>

      {roleDialog.open && (
        <TeamRoleDialog
          key={roleDialog.role?.id ?? 'create'}
          role={roleDialog.role}
          permissions={permissions.data ?? []}
          onClose={() => setRoleDialog({ open: false, role: null })}
          onSubmit={submitRole}
          isSubmitting={saveRole.isPending}
          errorMessage={errorText(saveRole.error)}
        />
      )}

      {isAddOpen && (
        <AddMemberDialog
          roles={roleList}
          onClose={() => setAddOpen(false)}
          onSubmit={(payload) => addMember.mutateAsync(payload)}
          isSubmitting={addMember.isPending}
        />
      )}

      <Dialog open={!!pending} onClose={closePending} fullWidth maxWidth="xs">
        <DialogTitle>
          {pending?.kind === 'member' ? t('dashboard.team.members.removeTitle') : t('dashboard.team.roles.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pending?.kind === 'member'
              ? t('dashboard.team.members.removeConfirm', { name: memberName(pending.member) })
              : pending
                ? t('dashboard.team.roles.deleteConfirm', { name: pending.role.name })
                : null}
          </Typography>
          {(removeMember.error || deleteRole.error) && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
              {errorText(removeMember.error ?? deleteRole.error)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <button type="button" className="secondary-action-btn" onClick={closePending}>
            {t('dashboard.organizations.cancel')}
          </button>
          <button
            type="button"
            className="danger-action-btn"
            onClick={() => void confirmPending()}
            disabled={removeMember.isPending || deleteRole.isPending}
          >
            {pending?.kind === 'member' ? t('dashboard.team.members.remove') : t('dashboard.common.delete')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamV2;
